import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, ChevronDown } from "lucide-react";
import { api, formatApiError } from "../api";
import Reveal from "./Reveal";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";

function fmtDate(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function displayFR(d) {
  if (!d) return "Sélectionner une date";
  return d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

const EMPTY = { name: "", email: "", phone: "", category: "", address: "", notes: "" };

export default function BookingSection() {
  const [form, setForm] = useState(EMPTY);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [openCal, setOpenCal] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (!date) { setSlots([]); return; }
    (async () => {
      try {
        const { data } = await api.get(`/appointments/slots?date=${fmtDate(date)}`);
        setSlots(data.available || []);
        setTime("");
      } catch (_e) { setSlots([]); }
    })();
  }, [date]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.category || !date || !time) {
      toast.error("Merci de remplir tous les champs obligatoires.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/appointments", { ...form, date: fmtDate(date), time });
      setDone(true);
      setForm(EMPTY); setDate(null); setTime("");
      toast.success("Rendez-vous demandé — nous vous confirmons rapidement !");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const today = new Date(); today.setHours(0, 0, 0, 0);

  return (
    <section id="rdv" className="relative bg-[#050505] py-24 sm:py-32" data-testid="booking-section">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-2 lg:items-start">
        <Reveal>
          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-white/50">Prise de rendez-vous</span>
          <h2 className="mt-4 font-head text-4xl font-semibold uppercase tracking-tight text-white sm:text-5xl md:text-6xl">
            Réservez votre <span className="text-gradient">intervention</span>
          </h2>
          <p className="mt-6 max-w-md text-base font-light leading-relaxed text-white/60">
            Choisissez une date et un créneau. Nous confirmons votre rendez-vous
            après validation de la disponibilité.
          </p>
          <div className="mt-10 space-y-4">
            <div className="flex items-start gap-4">
              <span className="mt-1 flex h-9 w-9 items-center justify-center rounded-md bg-brand-gradient">
                <CalendarClock size={16} className="text-white" />
              </span>
              <div>
                <p className="text-sm font-medium text-white">Créneaux 9h — 19h</p>
                <p className="text-xs text-white/50">Du lundi au samedi, sur rendez-vous</p>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="rounded-lg border border-white/10 bg-[#0f0f0f] p-6 sm:p-8">
            {done ? (
              <div className="py-10 text-center" data-testid="booking-success">
                <h3 className="font-head text-2xl font-medium uppercase text-white">Rendez-vous demandé</h3>
                <p className="mt-3 text-sm font-light text-white/60">Nous revenons vers vous pour confirmer.</p>
                <button onClick={() => setDone(false)} className="mt-8 rounded-full border border-white/20 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white hover:text-black" data-testid="booking-new-btn">
                  Nouveau rendez-vous
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="booking-form">
                <div className="grid gap-5 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-white/70">Nom complet *</Label>
                    <Input value={form.name} onChange={set("name")} placeholder="Jean Dupont" className="border-white/10 bg-black/50 text-white placeholder:text-white/30" data-testid="booking-input-name" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-white/70">Téléphone</Label>
                    <Input value={form.phone} onChange={set("phone")} placeholder="06 12 34 56 78" className="border-white/10 bg-black/50 text-white placeholder:text-white/30" data-testid="booking-input-phone" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-white/70">Email *</Label>
                  <Input type="email" value={form.email} onChange={set("email")} placeholder="vous@email.fr" className="border-white/10 bg-black/50 text-white placeholder:text-white/30" data-testid="booking-input-email" />
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-white/70">Catégorie *</Label>
                    <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                      <SelectTrigger className="border-white/10 bg-black/50 text-white" data-testid="booking-select-category">
                        <SelectValue placeholder="Choisir…" />
                      </SelectTrigger>
                      <SelectContent className="border-white/10 bg-[#111] text-white">
                        <SelectItem value="maintenance">Maintenance des bâtiments</SelectItem>
                        <SelectItem value="evenementiel">Événementiel</SelectItem>
                        <SelectItem value="autre">Autre demande</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-white/70">Adresse</Label>
                    <Input value={form.address} onChange={set("address")} placeholder="Adresse du site" className="border-white/10 bg-black/50 text-white placeholder:text-white/30" data-testid="booking-input-address" />
                  </div>
                </div>

                <div className="grid gap-5 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-white/70">Date *</Label>
                    <Popover open={openCal} onOpenChange={setOpenCal}>
                      <PopoverTrigger asChild>
                        <button type="button" className="flex w-full items-center justify-between rounded-md border border-white/10 bg-black/50 px-3 py-2 text-left text-sm text-white" data-testid="booking-date-btn">
                          <span className={date ? "text-white" : "text-white/40"}>{displayFR(date)}</span>
                          <ChevronDown size={16} className="text-white/50" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto border-white/10 bg-[#111] p-0 text-white" align="start">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={(d) => { setDate(d); setOpenCal(false); }}
                          disabled={(d) => d < today}
                          className="rounded-md"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-white/70">Créneau *</Label>
                    <Select value={time} onValueChange={setTime} disabled={!date || slots.length === 0}>
                      <SelectTrigger className="border-white/10 bg-black/50 text-white" data-testid="booking-select-time">
                        <SelectValue placeholder={date ? (slots.length ? "Choisir…" : "Aucun créneau") : "Choisir la date d'abord"} />
                      </SelectTrigger>
                      <SelectContent className="border-white/10 bg-[#111] text-white">
                        {slots.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-white/70">Notes</Label>
                  <Textarea value={form.notes} onChange={set("notes")} rows={3} placeholder="Précisez votre besoin…" className="border-white/10 bg-black/50 text-white placeholder:text-white/30" data-testid="booking-input-notes" />
                </div>

                <button type="submit" disabled={loading} className="w-full rounded-full bg-brand-gradient px-8 py-4 text-sm font-semibold text-white transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60" data-testid="booking-submit-btn">
                  {loading ? "Envoi…" : "Réserver mon créneau"}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
